<script setup lang="ts">
import { computed } from 'vue'
import {
  useDeleteChatMessageSendingJobMutation,
  type ChatMessageSendingJobDetailPageDeleteButtonFragment,
} from './ChatMessageSendingJobDetailPageDeleteButton.generated'
import { ChatMessageSendingJobStatus } from '@/types/schema.generated'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useRouter } from 'vue-router'
import { useAlert } from '@/libs/alertPortal'
import { wait } from '@/libs/utilities/wait'
import { captureException as newrelicCaptureException } from '@/newrelic'

const props = defineProps<{
  fragment: ChatMessageSendingJobDetailPageDeleteButtonFragment
}>()

const canDelete = computed(() => {
  return (
    props.fragment.chatMessageSendingJob?.status ===
    ChatMessageSendingJobStatus.ChatMessageSendingJobStatusPending
  )
})

const mutation = useDeleteChatMessageSendingJobMutation()
const router = useRouter()
const alert = useAlert()

const deleteChatMessageSendingJob = async (guid: string) => {
  const res = await mutation.mutate({
    input: {
      guid: guid,
    },
  })

  if (res?.errors) {
    throw res.errors
  }

  const data = res?.data?.deleteChatMessageSendingJob
  if (!data) {
    throw new Error('failed to get res.data')
  }

  if (data.__typename === 'DeleteChatMessageSendingJobErrorPayload') {
    alert.error(data.reason)
    throw new Error(data.reason)
  }

  if (data.__typename === 'DeleteChatMessageSendingJobSuccessPayload') {
    return data.jobGuid
  }

  return undefined
}

const deletionHandler = async () => {
  if (!props.fragment.chatMessageSendingJob?.guid) {
    return
  }

  if (
    !window.confirm(
      `配信予定を削除すると、データは完全に失われ、元に戻すことはできません。削除を実行しますか？`
    )
  ) {
    return
  }

  try {
    const guid = await deleteChatMessageSendingJob(
      props.fragment.chatMessageSendingJob.guid
    )
    if (guid) {
      alert.success('メッセージの配信予定を削除しました')

      // レプリカラグを考慮して1秒待つ
      await wait(1000)

      // eslint-disable-next-line fp/no-mutating-methods
      await router.push({
        name: 'chatMessageSendingJobIndex',
      })
    } else {
      alert.error('メッセージ配信予定の削除に失敗しました')
    }
  } catch (e) {
    console.error(e)

    newrelicCaptureException(e)
    if (e instanceof Error) {
      alert.error('エラーが発生しました')
    }
  }
}
</script>
<template>
  <FlatButton v-if="canDelete" variant="caution" @click="deletionHandler">
    削除
  </FlatButton>
</template>
